import React, { memo, useRef, useState } from 'react';

import { API } from 'constants';
import client from '../../../../services/library-api';

import { useFormState } from '../../../../hooks/useFormState';
import { useResponsiveBreakpoints } from '../../../../hooks/useResponsiveBreakpoints';
import { useSwrRequestKeysToMutateFromContext } from '../../../../hooks/useSwrRequestKeysToMutateFromContext';

import { megabytesToBytes } from '../../../../helpers/fileUtils';
import { capitalizeFirstLetter } from '../../../../services/strings';
import { mutateListDataAfterUpdatingItem } from '../../../../helpers/swrMutateUtils';

import { collectionColorOptions } from '../../../../constants/collections';

import FileInput from '../../../../design-system/FileInput/FileInput';
import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import ColorPicker from '../../../../design-system/ColorPicker/ColorPicker';
import FormFieldWrapper from '../../../../design-system/FormFieldWrapper/FormFieldWrapper';
import SectionContainer from '../SectionContainer/SectionContainer';
import InputWithAutoSaveOnBlur from '../../../../design-system/InputWithAutoSaveOnBlur/InputWithAutoSaveOnBlur';
import TextAreaWithAutoSaveOnBlur from '../../../../design-system/TextAreaWithAutoSaveOnBlur/TextAreaWithAutoSaveOnBlur';

const maxSizeInBytes = megabytesToBytes(5);

const AboutPlaybookSection = ({ playbookDetail, setPlaybookDetail, isEditable }) => {
    const { id, name, description, cover_image, icon_color } = playbookDetail;

    const formDataInitialState = {
        name,
        description,
        cover_image,
        icon_color,
    };
    const { formData, handleInputChange } = useFormState(formDataInitialState);
    const savedData = useRef(formDataInitialState);

    const colorController = useRef(new AbortController());
    const imageController = useRef(new AbortController());

    const [errorAlert, setErrorAlert] = useState(null);

    const route = `${API.ROUTES.library.playbook}${id}/`;

    const { isDesktop } = useResponsiveBreakpoints();

    const {
        requestKeysToMutate: { playbooks: requestKeysToMutate },
    } = useSwrRequestKeysToMutateFromContext();

    const makePatchRequest = async ({ field, value, controllerRef, isFile = false }) => {
        if (controllerRef) {
            controllerRef.current?.abort();
            controllerRef.current = new AbortController();
        }

        let patchData = {};
        let config = {};

        if (isFile) {
            const formData = new FormData();
            formData.append(field, value);
            patchData = formData;
        } else {
            patchData = { [field]: value };
        }

        if (controllerRef) {
            config.signal = controllerRef.current.signal;
        }

        const { data: bot } = await client.patch(route, patchData, config);

        setPlaybookDetail((detail) => ({ ...detail, [field]: bot[field] }));
        savedData.current[field] = bot[field];

        mutateListDataAfterUpdatingItem({
            id,
            requestKeysToMutate,
            updatedPart: { [field]: bot[field] },
        });
    };

    const handleSave = async (field) => {
        if (!isEditable) return;

        try {
            setPlaybookDetail((detail) => ({ ...detail, [field]: formData[field] }));

            await makePatchRequest({ field, value: formData[field] });
        } catch (e) {
            const message = `Oops! Something went wrong while editing ${capitalizeFirstLetter(
                field
            )}. Please try again.`;
            setErrorAlert({ message, statusCode: e.response?.status });
            handleInputChange(field, savedData.current[field]);
            setPlaybookDetail((detail) => ({ ...detail, [field]: savedData.current[field] }));

            throw new Error(e);
        }
    };

    const handleColorChange = async (color) => {
        if (!isEditable) return;
        if (!color) return;

        try {
            handleInputChange('icon_color', color);

            await makePatchRequest({
                field: 'icon_color',
                value: color,
                controllerRef: colorController,
            });
        } catch (e) {
            if (e.message === 'canceled') {
                return;
            }

            setErrorAlert({
                message: `Oops! Something went wrong while editing header color. Please try again.`,
                statusCode: e.response?.status,
            });
            handleInputChange('icon_color', playbookDetail.icon_color);
        }
    };

    const handleImageChange = async (image) => {
        if (!isEditable) return;
        if (!image) return;

        try {
            handleInputChange('cover_image', image);

            await makePatchRequest({
                field: 'cover_image',
                value: image,
                controllerRef: imageController,
                isFile: true,
            });
        } catch (e) {
            if (e.message === 'canceled') {
                return;
            }

            setErrorAlert({
                message: `Oops! Something went wrong while editing cover image. Please try again.`,
                statusCode: e.response?.status,
            });
            handleInputChange('cover_image', playbookDetail.cover_image);
        }
    };

    return (
        <SectionContainer
            title="About Playbook"
            variant="secondary"
            marginClassName="mb-2"
            paddingClassName="p-4 sm:p-5 md:p-6"
        >
            <div className="w-full max-w-full flex flex-col lg:flex-row gap-x-6 2xl:gap-x-8 3xl:gap-x-[52px] gap-y-2">
                <div className="lg:flex-1 flex flex-col gap-2">
                    <InputWithAutoSaveOnBlur
                        size="md"
                        name="playbook_name"
                        label="Name"
                        isRequired
                        value={formData.name}
                        originalValue={name}
                        placeholder="Enter Playbook name"
                        isDisabled={!isEditable}
                        handleInputChange={(e) => handleInputChange('name', e.target.value)}
                        handleSave={() => handleSave('name')}
                        displaySaveStatus={false}
                    />

                    <TextAreaWithAutoSaveOnBlur
                        name="playbook_description"
                        label="Description"
                        isRequired
                        value={formData.description}
                        originalValue={description}
                        isDisabled={!isEditable}
                        placeholder="Enter Playbook description"
                        handleChange={(e) => handleInputChange('description', e.target.value)}
                        handleSave={() => handleSave('description')}
                        height={120}
                    />

                    <ColorPicker
                        label="Select a header color"
                        options={collectionColorOptions}
                        selected={formData.icon_color}
                        handleSelect={handleColorChange}
                        isDisabled={!isEditable}
                    />
                </div>
                <div className="lg:flex-1">
                    <div className="max-w-[422px] 3xl:max-w-[480px]">
                        <FormFieldWrapper label="Cover Image" gap={isDesktop ? 20 : 8}>
                            <FileInput
                                handleFile={handleImageChange}
                                file={formData.cover_image}
                                formatsArray={['.jpg', '.jpeg', '.png']}
                                maxSizeInBytes={maxSizeInBytes}
                                placeholder="Drag & drop a file to upload or click to browse"
                                shouldShowPreview
                                state={isEditable ? 'default' : 'disabled'}
                            />
                        </FormFieldWrapper>
                    </div>
                </div>
            </div>

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </SectionContainer>
    );
};

export default memo(AboutPlaybookSection);
